/**
 * Ukrainian localization for Splyt MiniApp
 * Default language: Ukrainian (uk)
 */

export type Locale = 'uk' | 'en';

export interface Translations {
  // Navigation
  nav: {
    home: string;
    marketplace: string;
    swipes: string;
    chats: string;
    add: string;
    saved: string;
    profile: string;
    forYou: string;
    following: string;
  };
  
  // Common
  common: {
    search: string;
    filter: string;
    sort: string;
    popular: string;
    trending: string;
    new: string;
    loading: string;
    error: string;
    retry: string;
    cancel: string;
    confirm: string;
    save: string;
    delete: string;
    edit: string;
    share: string;
    back: string;
    next: string;
    done: string;
  };
  
  // Categories
  categories: {
    all: string;
    beats: string;
    covers: string;
    features: string;
    mixing: string;
    kits: string;
    services: string;
  };
  
  // Item detail
  itemDetail: {
    preview: string;
    buy: string;
    addToCart: string;
    format: string;
    license: string;
    description: string;
    tags: string;
    bpm: string;
    key: string;
    price: string;
    exclusive: string;
    follow: string;
    message: string;
  };
  
  // Upload
  upload: {
    title: string;
    selectCategory: string;
    addTitle: string;
    addDescription: string;
    uploadFile: string;
    uploadCover: string;
    setPrice: string;
    submit: string;
    processing: string;
    success: string;
    error: string;
  };
  
  // Purchase
  purchase: {
    buyNow: string;
    checkout: string;
    processing: string;
    success: string;
    failed: string;
    confirmPurchase: string;
    total: string;
  };
  
  // Profile
  profile: {
    yourProfile: string;
    creatorMode: string;
    sales: string;
    earnings: string;
    uploads: string;
    settings: string;
    logout: string;
  };
  
  // Swipes
  swipes: {
    discover: string;
    likeToSave: string;
    swipeToDiscover: string;
    noMoreItems: string;
  };
  
  // Messages
  messages: {
    welcomeToSplyt: string;
    itemNotAvailable: string;
    networkError: string;
    authError: string;
    uploadSuccess: string;
    purchaseSuccess: string;
  };
}

const translations: Record<Locale, Translations> = {
  uk: {
    nav: {
      home: 'Головна',
      marketplace: 'Маркетплейс',
      swipes: 'Свайпи',
      chats: 'Чати',
      add: 'Додати',
      saved: 'Збережені',
      profile: 'Профіль',
      forYou: 'Для тебе',
      following: 'Підписки',
    },
    common: {
      search: 'Пошук',
      filter: 'Фільтри',
      sort: 'Сортування',
      popular: 'Популярні',
      trending: 'Трендові',
      new: 'Нові',
      loading: 'Завантаження...',
      error: 'Помилка',
      retry: 'Спробувати ще раз',
      cancel: 'Скасувати',
      confirm: 'Підтвердити',
      save: 'Зберегти',
      delete: 'Видалити',
      edit: 'Редагувати',
      share: 'Поділитися',
      back: 'Назад',
      next: 'Далі',
      done: 'Готово',
    },
    categories: {
      all: 'Всі',
      beats: 'Біти',
      covers: 'Дизайн',
      features: 'Фіти',
      mixing: 'Мікс/Мастерінг',
      kits: 'Кіти',
      services: 'Послуги',
    },
    itemDetail: {
      preview: 'Прослухати',
      buy: 'Купити',
      addToCart: 'Додати в кошик',
      format: 'Формат',
      license: 'Ліцензія',
      description: 'Опис',
      tags: 'Теги',
      bpm: 'BPM',
      key: 'Тональність',
      price: 'Ціна',
      exclusive: 'Ексклюзив',
      follow: 'Підписатися',
      message: 'Написати',
    },
    upload: {
      title: 'Завантажити контент',
      selectCategory: 'Оберіть категорію',
      addTitle: 'Назва',
      addDescription: 'Опис',
      uploadFile: 'Завантажити файл',
      uploadCover: 'Завантажити обкладинку',
      setPrice: 'Встановити ціну',
      submit: 'Опублікувати',
      processing: 'Обробка...',
      success: 'Успішно опубліковано!',
      error: 'Помилка завантаження',
    },
    purchase: {
      buyNow: 'Купити зараз',
      checkout: 'Оформити покупку',
      processing: 'Обробка платежу...',
      success: 'Покупку завершено!',
      failed: 'Платіж не вдався',
      confirmPurchase: 'Підтвердити покупку?',
      total: 'Всього',
    },
    profile: {
      yourProfile: 'Ваш профіль',
      creatorMode: 'Режим творця',
      sales: 'Продажі',
      earnings: 'Заробіток',
      uploads: 'Завантажені',
      settings: 'Налаштування',
      logout: 'Вийти',
    },
    swipes: {
      discover: 'Відкривай нове',
      likeToSave: 'Свайпни вправо, щоб зберегти',
      swipeToDiscover: 'Свайпай, щоб відкривати новий контент',
      noMoreItems: 'Наразі більше немає контенту',
    },
    messages: {
      welcomeToSplyt: 'Ласкаво просимо до Splyt! 🎵',
      itemNotAvailable: 'Цей елемент більше недоступний',
      networkError: 'Помилка мережі. Перевірте підключення.',
      authError: 'Помилка авторизації. Спробуйте перезапустити додаток.',
      uploadSuccess: 'Контент успішно завантажено!',
      purchaseSuccess: 'Покупку завершено! Перевірте повідомлення від бота.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      marketplace: 'Marketplace',
      swipes: 'Swipes',
      chats: 'Chats',
      add: 'Add',
      saved: 'Saved',
      profile: 'Profile',
      forYou: 'For You',
      following: 'Following',
    },
    common: {
      search: 'Search',
      filter: 'Filters',
      sort: 'Sort',
      popular: 'Popular',
      trending: 'Trending',
      new: 'New',
      loading: 'Loading...',
      error: 'Error',
      retry: 'Retry',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      share: 'Share',
      back: 'Back',
      next: 'Next',
      done: 'Done',
    },
    categories: {
      all: 'All',
      beats: 'Beats',
      covers: 'Design',
      features: 'Features',
      mixing: 'Mixing/Mastering',
      kits: 'Kits',
      services: 'Services',
    },
    itemDetail: {
      preview: 'Preview',
      buy: 'Buy',
      addToCart: 'Add to Cart',
      format: 'Format',
      license: 'License',
      description: 'Description',
      tags: 'Tags',
      bpm: 'BPM',
      key: 'Key',
      price: 'Price',
      exclusive: 'Exclusive',
      follow: 'Follow',
      message: 'Message',
    },
    upload: {
      title: 'Upload Content',
      selectCategory: 'Select Category',
      addTitle: 'Title',
      addDescription: 'Description',
      uploadFile: 'Upload File',
      uploadCover: 'Upload Cover',
      setPrice: 'Set Price',
      submit: 'Publish',
      processing: 'Processing...',
      success: 'Successfully published!',
      error: 'Upload error',
    },
    purchase: {
      buyNow: 'Buy Now',
      checkout: 'Checkout',
      processing: 'Processing payment...',
      success: 'Purchase completed!',
      failed: 'Payment failed',
      confirmPurchase: 'Confirm purchase?',
      total: 'Total',
    },
    profile: {
      yourProfile: 'Your Profile',
      creatorMode: 'Creator Mode',
      sales: 'Sales',
      earnings: 'Earnings',
      uploads: 'Uploads',
      settings: 'Settings',
      logout: 'Logout',
    },
    swipes: {
      discover: 'Discover',
      likeToSave: 'Swipe right to save',
      swipeToDiscover: 'Swipe to discover amazing content',
      noMoreItems: 'No more items for now',
    },
    messages: {
      welcomeToSplyt: 'Welcome to Splyt! 🎵',
      itemNotAvailable: 'This item is no longer available',
      networkError: 'Network error. Check your connection.',
      authError: 'Authorization error. Try restarting the app.',
      uploadSuccess: 'Content uploaded successfully!',
      purchaseSuccess: 'Purchase completed! Check bot messages.',
    },
  },
};

// Get current locale from user or default to Ukrainian
export const getCurrentLocale = (): Locale => {
  const saved = localStorage.getItem('splyt_locale');
  return (saved as Locale) || 'uk';
};

// Set locale
export const setLocale = (locale: Locale) => {
  localStorage.setItem('splyt_locale', locale);
};

// Get translations for current locale
export const t = (): Translations => {
  const locale = getCurrentLocale();
  return translations[locale];
};

// Format currency in UAH
export const formatPrice = (price: number): string => {
  return `${price.toFixed(2)} ₴`;
};

export default {
  getCurrentLocale,
  setLocale,
  t,
  formatPrice,
};



